import type { FormEvent } from "react";
import { useState } from "react";

import { MailIcon, SuccessIcon } from "@/assets/icons";
import { Button, Input } from "@/atoms";
import { CopyableLink } from "@/components";
import { strings } from "@/constants";
import {
    AlertVariantEnum,
    ButtonTypeEnum,
    ButtonVariantEnum,
    InputTypeEnum,
} from "@/enums";
import { useCreateInvitation, useToast } from "@/hooks";
import type { InvitationType } from "@/types";

const validateEmailHandler = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const AdminSendInvitePage = () => {
    const [email, setEmail] = useState("");

    const [latestInvitation, setLatestInvitation] = useState<InvitationType | null>(null);

    const { onShow: showToastHandler } = useToast();

    const {
        isLoading: isSubmitting,
        onRun: runCreateInvitationHandler,
    } = useCreateInvitation();

    const submitHandler = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!validateEmailHandler(email)) {
            showToastHandler({
                message: strings.validation.emailInvalid,
                variant: AlertVariantEnum.ERROR,
            });

            return;
        }

        try {
            const created = await runCreateInvitationHandler({ email });

            setLatestInvitation(created.invitation);

            showToastHandler({
                message: `${strings.invite.invitePrefix}${email}`,
                variant: AlertVariantEnum.SUCCESS,
            });

            setEmail("");
        } catch (error) {
            showToastHandler({
                message: error instanceof Error ? error.message : strings.errors.tryAgain,
                variant: AlertVariantEnum.ERROR,
            });
        }
    };

    return (
        <div
            className="
                mx-auto
                w-full
                max-w-xl
                space-y-6
            "
        >
            <div className="flex items-center gap-2 sm:gap-3">
                <MailIcon className="h-6 w-6 shrink-0 text-primary sm:h-7 sm:w-7" />
                <h1 className="text-xl font-bold text-text-primary sm:text-2xl">{strings.admin.sendInviteTitle}</h1>
            </div>
            <div
                className="
                    space-y-5
                    rounded-default
                    border
                    border-gray-200
                    bg-surface
                    p-4
                    shadow-sm
                    sm:p-6
                "
            >
                <p className="text-sm text-text-secondary">{strings.admin.sendInviteDescription}</p>
                <form
                    className="space-y-4"
                    onSubmit={submitHandler}
                >
                    <Input
                        disabled={isSubmitting}
                        label={strings.admin.sendInviteEmailLabel}
                        placeholder={strings.admin.sendInviteEmailPlaceholder}
                        type={InputTypeEnum.EMAIL}
                        value={email}
                        required
                        onChange={({ target }) => setEmail(target.value)}
                    />
                    <Button
                        isLoading={isSubmitting}
                        type={ButtonTypeEnum.SUBMIT}
                        variant={ButtonVariantEnum.PRIMARY}
                        isFullWidth
                    >
                        {strings.admin.sendInviteSubmit}
                    </Button>
                </form>
            </div>
            {latestInvitation?.registrationUrl && (
                <div
                    className="
                        space-y-4
                        rounded-default
                        border
                        border-primary/30
                        bg-primary/5
                        p-4
                        sm:p-6
                    "
                >
                    <div
                        className="
                            flex
                            items-start
                            gap-3
                        "
                    >
                        <span
                            className="
                                flex
                                size-9
                                shrink-0
                                items-center
                                justify-center
                                rounded-full
                                bg-primary
                                text-white
                            "
                        >
                            <SuccessIcon className="size-5" />
                        </span>
                        <div className="space-y-1">
                            <p
                                className="
                                    text-base
                                    font-semibold
                                    text-text-primary
                                "
                            >
                                {strings.invite.registrationLinkSuccessTitle}
                            </p>
                            <p
                                className="
                                    text-sm
                                    text-text-secondary
                                "
                            >
                                {strings.invite.registrationLinkDescription}
                            </p>
                        </div>
                    </div>
                    <CopyableLink
                        label={strings.invite.registrationLinkLabel}
                        url={latestInvitation.registrationUrl}
                    />
                </div>
            )}
        </div>
    );
};
